import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import styles from './LandingProfileAchievements.module.css'

export function AchievementCategory({
  title,
  description,
  earnedCount,
  totalCount,
  children,
}: {
  title: string
  description: string
  earnedCount: number
  totalCount: number
  children: ReactNode
}) {
  const id = useId()
  const track = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  const descriptionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [navigation, setNavigation] = useState({ previous: false, next: false })

  useEffect(
    () => () => {
      if (descriptionTimer.current !== null) clearTimeout(descriptionTimer.current)
    },
    []
  )

  function showDescription() {
    if (descriptionTimer.current !== null) clearTimeout(descriptionTimer.current)
    setDescriptionOpen(true)
    descriptionTimer.current = setTimeout(() => {
      setDescriptionOpen(false)
      descriptionTimer.current = null
    }, 3000)
  }

  useEffect(() => {
    const element = track.current
    if (!element) return
    const update = () =>
      setNavigation({
        previous: element.scrollLeft > 1,
        next: element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
      })
    update()
    element.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
    observer?.observe(element)
    return () => {
      element.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [children, expanded])

  function navigate(direction: number) {
    const element = track.current
    element?.scrollBy({
      left: direction * Math.max(100, element.clientWidth * 0.75),
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    })
  }

  return (
    <section className={styles.category} aria-labelledby={`${id}-title`}>
      <div className={styles.categoryHeader}>
        <div className={styles.categoryTitle}>
          <h3 id={`${id}-title`} className={styles.scopeTitle}>
            {title}
          </h3>
          <span
            className={styles.categoryCount}
            aria-label={`${earnedCount} de ${totalCount} conquistados — ${title}`}
          >
            <strong>{earnedCount}</strong>
            <span>/{totalCount}</span>
          </span>
          <button
            type="button"
            className={styles.categoryInfo}
            aria-label={`Sobre ${title}`}
            aria-expanded={descriptionOpen}
            aria-controls={`${id}-description`}
            aria-describedby={descriptionOpen ? `${id}-description` : undefined}
            onClick={showDescription}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                if (descriptionTimer.current !== null)
                  clearTimeout(descriptionTimer.current)
                setDescriptionOpen(false)
              }
            }}
          >
            <Icon icon="mdi:information-outline" aria-hidden />
          </button>
          {descriptionOpen && (
            <p
              id={`${id}-description`}
              className={styles.categoryDescription}
              role="tooltip"
            >
              {description}
            </p>
          )}
        </div>
        <div className={styles.categoryActions}>
          {!expanded && (
            <>
              <button
                type="button"
                aria-label={`Selos anteriores — ${title}`}
                aria-controls={`${id}-track`}
                disabled={!navigation.previous}
                onClick={() => navigate(-1)}
              >
                <Icon icon="mdi:chevron-left" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Próximos selos — ${title}`}
                aria-controls={`${id}-track`}
                disabled={!navigation.next}
                onClick={() => navigate(1)}
              >
                <Icon icon="mdi:chevron-right" aria-hidden />
              </button>
            </>
          )}
          <button
            type="button"
            className={styles.viewAll}
            aria-label={`${expanded ? 'Recolher' : 'Ver todos'} — ${title}`}
            aria-expanded={expanded}
            aria-controls={`${id}-track`}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Recolher' : 'Ver todos'}
          </button>
        </div>
      </div>
      <div
        id={`${id}-track`}
        ref={track}
        className={`${styles.badges} ${expanded ? styles.badgesExpanded : ''}`}
      >
        {children}
      </div>
    </section>
  )
}
